/**
 * ConsolidatedDashboard - Visão consolidada de todas as empresas
 * Mostra métricas agregadas e comparativo por empresa
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout, companyMenuItems } from '../../components/DashboardLayout';
import { companiesAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { 
  Users, 
  Briefcase, 
  ClipboardCheck, 
  Brain,
  Building2,
  TrendingUp,
  Loader2,
  ArrowRight,
  LayoutGrid,
  Crown
} from 'lucide-react';

export default function ConsolidatedDashboard() {
  const navigate = useNavigate();
  const { selectCompany, managedCompanies } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await companiesAPI.getConsolidatedDashboard();
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCompany = (company) => {
    if (company.is_principal) {
      selectCompany(null);
      navigate('/company');
    } else {
      // Encontrar empresa gerenciada correspondente
      const managed = managedCompanies.find(m => m.company_id === company.id);
      if (managed) {
        selectCompany(managed);
        navigate('/company');
      }
    }
  };

  if (loading) {
    return (
      <DashboardLayout menuItems={companyMenuItems}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout menuItems={companyMenuItems}>
        <div className="text-center py-12">
          <p className="text-red-500">{error}</p>
          <Button onClick={fetchData} className="mt-4">Tentar novamente</Button>
        </div>
      </DashboardLayout>
    );
  }

  const { totals, companies, total_empresas } = data || {};

  // Calcular porcentagens para gráfico
  const maxFuncionarios = Math.max(...(companies || []).map(c => c.stats?.funcionarios || 0), 1);

  return (
    <DashboardLayout menuItems={companyMenuItems}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <LayoutGrid className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Visão Geral</h1>
              <p className="text-slate-500">
                {total_empresas} empresa{total_empresas !== 1 ? 's' : ''} • Métricas consolidadas
              </p>
            </div>
          </div>
          <Button 
            variant="outline" 
            onClick={() => { selectCompany(null); navigate('/company'); }}
          >
            <Building2 className="w-4 h-4 mr-2" />
            Voltar para Empresa Principal
          </Button>
        </div>

        {/* Cards de Totais */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-cyan-50 to-white border-cyan-100">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-cyan-600">Total de Funcionários</p>
                  <p className="text-3xl font-bold text-cyan-900">{totals?.funcionarios || 0}</p>
                  <p className="text-xs text-cyan-600">{totals?.funcionarios_ativos || 0} ativos</p>
                </div>
                <div className="p-3 bg-cyan-100 rounded-lg">
                  <Users className="w-6 h-6 text-cyan-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-100">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-600">Vagas Ativas</p>
                  <p className="text-3xl font-bold text-purple-900">{totals?.vagas_ativas || 0}</p>
                  <p className="text-xs text-purple-600">em todas as empresas</p>
                </div>
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Briefcase className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-50 to-white border-amber-100">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-amber-600">Avaliações Pendentes</p>
                  <p className="text-3xl font-bold text-amber-900">{totals?.avaliacoes_pendentes || 0}</p>
                  <p className="text-xs text-amber-600">aguardando resposta</p>
                </div>
                <div className="p-3 bg-amber-100 rounded-lg">
                  <ClipboardCheck className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-white border-green-100">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-600">Grupos de Teste</p>
                  <p className="text-3xl font-bold text-green-900">{totals?.grupos_teste || 0}</p>
                  <p className="text-xs text-green-600">{totals?.testes_pendentes || 0} testes pendentes</p>
                </div>
                <div className="p-3 bg-green-100 rounded-lg">
                  <Brain className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Comparativo por Empresa */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-cyan-600" />
              Comparativo por Empresa
            </CardTitle>
            <CardDescription>
              Clique em uma empresa para acessar seu dashboard
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {companies?.map((company) => (
                <div 
                  key={company.id}
                  className={`p-4 rounded-lg border transition-all cursor-pointer hover:shadow-md ${
                    company.is_principal 
                      ? 'bg-cyan-50 border-cyan-200 hover:bg-cyan-100' 
                      : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
                  }`}
                  onClick={() => handleSelectCompany(company)}
                  data-testid={`company-card-${company.id}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${company.is_principal ? 'bg-cyan-100' : 'bg-slate-200'}`}>
                        {company.is_principal ? (
                          <Crown className="w-4 h-4 text-cyan-600" />
                        ) : (
                          <Building2 className="w-4 h-4 text-slate-500" />
                        )}
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900 flex items-center gap-2">
                          {company.nome}
                          {company.is_principal && (
                            <span className="text-xs bg-cyan-600 text-white px-2 py-0.5 rounded-full">
                              Principal
                            </span>
                          )}
                        </p>
                        {company.cnpj && (
                          <p className="text-xs text-slate-500">
                            CNPJ: {company.cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')}
                          </p>
                        )}
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" className="text-cyan-600">
                      Acessar <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>

                  {/* Métricas da empresa */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                    <div className="text-center p-2 bg-white rounded border">
                      <p className="text-lg font-bold text-cyan-600">{company.stats?.funcionarios || 0}</p>
                      <p className="text-xs text-slate-500">Funcionários</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded border">
                      <p className="text-lg font-bold text-purple-600">{company.stats?.vagas_ativas || 0}</p>
                      <p className="text-xs text-slate-500">Vagas</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded border">
                      <p className="text-lg font-bold text-amber-600">{company.stats?.avaliacoes_pendentes || 0}</p>
                      <p className="text-xs text-slate-500">Avaliações</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded border">
                      <p className="text-lg font-bold text-green-600">{company.stats?.grupos_teste || 0}</p>
                      <p className="text-xs text-slate-500">Grupos</p>
                    </div>
                  </div>

                  {/* Barra de proporção de funcionários */}
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs text-slate-500">
                      <span>Funcionários</span>
                      <span>{Math.round((company.stats?.funcionarios || 0) / (totals?.funcionarios || 1) * 100)}% do total</span>
                    </div>
                    <Progress 
                      value={(company.stats?.funcionarios || 0) / maxFuncionarios * 100} 
                      className="h-2"
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
